/**
 * Two-step 10-team list builder: select by category (seeds / intermediates / outsiders),
 * then rank via drag-and-drop. Used by EditCountryListModal and Rules demos; readOnly for marketing.
 */
import { DragDropContext, Draggable, type DropResult, Droppable, type ResponderProvided } from 'react-beautiful-dnd';
import { type MouseEventHandler, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Center,
  Divider,
  Flex,
  HStack,
  Heading,
  SimpleGrid,
  VStack,
  Text,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  type Country,
  countryCategory,
  countryListValid,
  tournamentCountriesInfo,
  countriesInfo,
} from '@/models/country';
import {
  EDIT_LIST_MOBILE_RANK_ROW_PX,
  EDIT_LIST_MOBILE_SELECTION_FLAG_FILL,
} from '@/constants/editListMobile';
import {
  minSelectionCardWidthPx,
  selectionColumnsForWidth,
} from '@/helpers/fixtures/editListSelectionLayout';
import { Flag } from './Flag';
import { CountryTag } from './CountryTag';

const RANK_ROW_COUNT = 10;
/** Flag height as a fraction of each selection card / rank row (uniform within that column). */
const EDIT_LIST_FLAG_BOX_FILL = 0.75;
/** Upper bound on auto column count (still constrained to divisors of each section size). */
const SELECTION_GRID_COLUMNS = 12;

type SelectionGridColumns = {
  seeds: number;
  intermediates: number;
  outsiders: number;
};

const DEFAULT_SELECTION_GRID_COLUMNS: SelectionGridColumns = {
  seeds: 3,
  intermediates: 4,
  outsiders: 3,
};

/** Rules rank rows can shrink to fit the selection column (Seeds → Outsiders). */
const RANK_ALIGN_ROW_MIN_PX = 24;

const RANK_LAYOUT_DEFAULT = {
  gapPx: 8,
  stackSpacing: 2 as const,
  rowMinPx: 36,
  rowMaxPx: 72,
  numberSize: 'md' as const,
  rankColumnWidth: { lg: '16rem' },
  rankColumnMinW: { lg: '15rem' },
  cardMinW: '9rem',
};

/** Rules “Rank Your Teams” preview — wider rank column so names fit on one line. */
const RANK_LAYOUT_COMPACT = {
  gapPx: 6,
  stackSpacing: 2 as const,
  rowMinPx: 28,
  rowMaxPx: 80,
  numberSize: 'sm' as const,
  rankColumnWidth: { lg: '14.375rem' },
  rankColumnMinW: { lg: '13.125rem' },
  cardMinW: '9.375rem',
};

// --- Helper components: ToggleCountryCard, EditListCountryCard ---

/** Matches the “Seeds (max 4)” row on the selection column so rank cards line up with the first seed row. */
function SeedsSectionLabel(props: { hidden?: boolean }) {
  return (
    <Heading
      fontSize='sm'
      textAlign='start'
      width='full'
      flexShrink={0}
      visibility={props.hidden ? 'hidden' : undefined}
      aria-hidden={props.hidden ? true : undefined}
    >
      Seeds (max 4)
    </Heading>
  );
}

function editListFlagHeightFromBox(boxHeightPx: number, fill: number, verticalPadPx = 8): number {
  const minPx = fill < 0.5 ? 8 : 12;
  return Math.max(minPx, Math.round((boxHeightPx - verticalPadPx) * fill));
}

function ToggleCountryCard(props: {
  country: Country;
  isSelected: boolean;
  onClick: MouseEventHandler;
  canBeSelected: boolean;
  readOnly?: boolean;
  flagBoxFill: number;
  labelFontSize?: number;
  minCardHeight?: number;
  cardPy?: number;
  nameLines?: number;
  nameSingleLine?: boolean;
  cardGap?: number;
  cardPx?: number;
}) {
  const countryInfo = countriesInfo[props.country];
  const defaultNameFontSize = useBreakpointValue({ base: 11, sm: 12 }) ?? 11;
  const nameFontSize = props.labelFontSize ?? defaultNameFontSize;
  const cardBg = useColorModeValue('white', 'gray.600');
  const idleBorder = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const cardRef = useRef<HTMLButtonElement>(null);
  const [cardHeightPx, setCardHeightPx] = useState(40);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const measure = () => setCardHeightPx(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [countryInfo.name]);

  const flagHeightPx = editListFlagHeightFromBox(cardHeightPx, props.flagBoxFill);
  const isDisabled = props.readOnly || !(props.isSelected || props.canBeSelected);

  return (
    <Box
      as='button'
      type='button'
      ref={cardRef}
      disabled={isDisabled}
      cursor={props.readOnly ? 'default' : isDisabled ? 'not-allowed' : 'pointer'}
      h='auto'
      minH={props.minCardHeight ?? 12}
      width='full'
      onClick={props.onClick}
      py={props.cardPy ?? 1.5}
      pl={props.cardPx ?? 2}
      pr={props.cardPx ?? 2}
      display='flex'
      flexDirection='row'
      alignItems='center'
      gap={props.cardGap ?? (props.labelFontSize != null ? 1.5 : 2)}
      whiteSpace={props.nameSingleLine ? 'nowrap' : 'normal'}
      bg={cardBg}
      borderWidth='2px'
      borderStyle='solid'
      borderColor={props.isSelected ? 'green.500' : idleBorder}
      borderRadius='md'
      opacity={isDisabled ? 0.4 : 1}
      _hover={
        props.readOnly || isDisabled
          ? { bg: cardBg }
          : {
              borderColor: props.isSelected ? 'green.600' : 'green.400',
              bg: cardBg,
            }
      }
      _active={{ bg: cardBg }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'blue.400', outlineOffset: '2px' }}
    >
      <Flex flexShrink={0} alignItems='center' justifyContent='flex-start'>
        <Flag
          country={countryInfo.id}
          borderRadius={2}
          h={`${flagHeightPx}px`}
          flexShrink={0}
          hasShadow={props.isSelected}
        />
      </Flex>
      <Flex flex={1} minW={0} alignItems='center' justifyContent='flex-start'>
        <Text
          width='full'
          textAlign='left'
          fontSize={nameFontSize}
          fontWeight='semibold'
          lineHeight={1.45}
          noOfLines={props.nameSingleLine ? 1 : (props.nameLines ?? 3)}
          overflow={props.nameSingleLine ? 'hidden' : undefined}
          textOverflow={props.nameSingleLine ? 'ellipsis' : undefined}
        >
          {countryInfo.name}
        </Text>
      </Flex>
    </Box>
  );
}

export function EditListCountryCard(props: {
  country: Country;
  index: number;
  isDisabled: boolean;
  rankList?: boolean;
  rowHeightPx: number;
  cardMinW?: string;
  flagBoxFill: number;
  nameSingleLine?: boolean;
  labelFontSize?: number;
  touchDragHandle?: boolean;
}) {
  const countryInfo = countriesInfo[props.country];
  const h = `${props.rowHeightPx}px`;

  return (
    <Draggable
      key={countryInfo.id}
      draggableId={countryInfo.name}
      index={props.index}
      isDragDisabled={props.isDisabled}
    >
      {(provided) => (
        <Box
          {...provided.draggableProps}
          {...(props.isDisabled ? {} : provided.dragHandleProps)}
          ref={provided.innerRef}
          width='full'
          minW={props.cardMinW ?? '9rem'}
          h={h}
          minH={h}
          maxH={h}
          flexShrink={0}
          display='flex'
          flexDirection='column'
        >
          <Box flex={1} minH={0} minW={0} w='full' h='full' display='flex' alignItems='stretch'>
            <CountryTag
              country={countryInfo.id}
              opaque
              hasShadow={!props.isDisabled}
              draggable={!props.isDisabled}
              compact
              rankList={props.rankList}
              flagBoxFill={props.flagBoxFill}
              fillHeight
              rowHeightPx={props.rowHeightPx}
              nameSingleLine={props.nameSingleLine}
              labelFontSize={props.labelFontSize}
              touchDragHandle={props.touchDragHandle}
            />
          </Box>
        </Box>
      )}
    </Draggable>
  );
}

// --- EditCountryListView: layout, measurement, toggleCountry ---

export function EditCountryListView(props: {
  countryList: Country[];
  setCountryList: (list: Country[]) => void;
  mobileProps?: {
    panel: 'selection' | 'ranking';
  };
  /** No toggles or drag; for Rules / marketing previews. */
  readOnly?: boolean;
  /** Hide “1. Select…” / “2. Rank…” headings (e.g. when embedded in Rules tab). */
  hideStepHeadings?: boolean;
  /** Tighter rank column for Rules preview (fits 10 rows without scrolling). */
  compactRank?: boolean;
  /** Teams per row in Seeds / Intermediates / Outsiders grids (default 6). */
  selectionColumns?: number;
  /** Flag height fraction for rank rows (default: same as selection). */
  rankFlagBoxFill?: number;
  /** Keep rank names on one line (Rules preview). */
  rankNameSingleLine?: boolean;
  /** Rank label size in px (Rules preview). */
  rankLabelFontSize?: number;
  /** Selection-grid flag height fraction (default: edit list). */
  selectionFlagBoxFill?: number;
  /** Selection-grid label size in px. */
  selectionLabelFontSize?: number;
  /**
   * Rules preview: match rank row spacing to the selection column (Seeds → Outsiders)
   * and use the same flag/text sizing as the selection grid.
   */
  alignRankToSelection?: boolean;
}) {
  const countryList = props.countryList;
  const readOnly = props.readOnly ?? false;
  const listValid = countryListValid(countryList);
  const setCountryList = readOnly ? () => {} : props.setCountryList;
  const isMobileEdit = props.mobileProps != null;
  const isMobileRanking = props.mobileProps?.panel === 'ranking';
  const isDesktopRank = !isMobileEdit;
  const helperTextColor = useColorModeValue('gray.600', 'gray.400');
  const rankLayout = props.compactRank ? RANK_LAYOUT_COMPACT : RANK_LAYOUT_DEFAULT;
  const rankStackSpacing = rankLayout.stackSpacing;
  const selectionColumns = props.selectionColumns ?? SELECTION_GRID_COLUMNS;
  const selectionGridGap = useBreakpointValue({ base: 1.5, md: 2 }) ?? 2;
  const selectionSectionSpacing = useBreakpointValue({ base: 2, md: 3 }) ?? 3;

  const defaultSelectionLabelSize = useBreakpointValue({ base: 11, sm: 12 }) ?? 11;
  const mobileSelectionLabelSize = useBreakpointValue({ base: 10, sm: 11 });
  const mobileRankLabelSize = useBreakpointValue({ base: 11, sm: 12 });

  const selectionTeamCounts = useMemo(() => {
    let seeds = 0;
    let intermediates = 0;
    let outsiders = 0;
    for (const cr of Object.values(tournamentCountriesInfo)) {
      if (cr.category === countryCategory.Favourite) seeds += 1;
      else if (cr.category === countryCategory.Intermediate) intermediates += 1;
      else if (cr.category === countryCategory.Outsider) outsiders += 1;
    }
    return { seeds, intermediates, outsiders };
  }, []);

  const selectionFlagBoxFill =
    props.selectionFlagBoxFill ??
    (isMobileEdit ? EDIT_LIST_MOBILE_SELECTION_FLAG_FILL : EDIT_LIST_FLAG_BOX_FILL);
  const selectionLabelFontSize =
    props.selectionLabelFontSize ?? (isMobileEdit ? mobileSelectionLabelSize : undefined);
  const rankFlagBoxFill = props.rankFlagBoxFill ?? selectionFlagBoxFill;
  const rankLabelFontSize =
    props.rankLabelFontSize ??
    (isMobileRanking ? mobileRankLabelSize : selectionLabelFontSize);
  const rankNameSingleLine = props.rankNameSingleLine ?? isMobileRanking;
  const selectionCompact =
    selectionLabelFontSize != null || props.selectionFlagBoxFill != null || isMobileEdit;
  const alignRankToSelection = props.alignRankToSelection ?? false;
  const rankListCompactLayout = props.compactRank || alignRankToSelection;
  const rankNumberSize =
    alignRankToSelection ? 'sm' : isDesktopRank ? rankLayout.numberSize : 'sm';

  const rankBodyRef = useRef<HTMLDivElement>(null);
  const countryBodyRef = useRef<HTMLDivElement>(null);
  const rankColumnListRef = useRef<HTMLDivElement>(null);
  const selectionAreaRef = useRef<HTMLDivElement>(null);
  const rankSeedsSpacerRef = useRef<HTMLDivElement>(null);
  const [rankRowHeightPx, setRankRowHeightPx] = useState(46);
  const [selectionGridColumns, setSelectionGridColumns] =
    useState<SelectionGridColumns>(DEFAULT_SELECTION_GRID_COLUMNS);

  /** Rules preview: fixed columns so rank rows stay aligned with the selection column. */
  const alignedSelectionGridColumns: SelectionGridColumns = {
    seeds: selectionColumns,
    intermediates: selectionColumns,
    outsiders: selectionColumns,
  };
  const effectiveSelectionGridColumns = alignRankToSelection
    ? alignedSelectionGridColumns
    : selectionGridColumns;

  /** Hidden “Seeds” label aligns rank #1 with the seed grid (edit modal + Rules preview). */
  const showRankSeedsSpacer =
    alignRankToSelection || (!props.hideStepHeadings && !props.compactRank);

  const measureRankRows = () => {
    if (isMobileRanking) {
      setRankRowHeightPx(EDIT_LIST_MOBILE_RANK_ROW_PX);
      return;
    }
    const rowMinPx = alignRankToSelection ? RANK_ALIGN_ROW_MIN_PX : rankLayout.rowMinPx;
    let slot: number;

    if (alignRankToSelection && selectionAreaRef.current) {
      const selectionH = selectionAreaRef.current.getBoundingClientRect().height;
      const seedsSpacerH = rankSeedsSpacerRef.current?.getBoundingClientRect().height ?? 0;
      slot = selectionH - seedsSpacerH;
    } else if (rankListCompactLayout && rankColumnListRef.current) {
      slot = rankColumnListRef.current.clientHeight;
    } else {
      const a = rankBodyRef.current?.clientHeight ?? 0;
      const b = countryBodyRef.current?.clientHeight ?? 0;
      slot = Math.min(a, b) || a || b;
    }

    if (slot < rowMinPx) return;
    const gaps = (RANK_ROW_COUNT - 1) * rankLayout.gapPx;
    let rowPx = Math.floor((slot - gaps) / RANK_ROW_COUNT);
    rowPx = Math.max(rowMinPx, Math.min(rowPx, rankLayout.rowMaxPx));
    while (RANK_ROW_COUNT * rowPx + gaps > slot && rowPx > rowMinPx) {
      rowPx -= 1;
    }
    setRankRowHeightPx(rowPx);
  };

  const measureSelectionColumns = () => {
    if (alignRankToSelection) return;

    const isSelectionVisible = !isMobileEdit || props.mobileProps?.panel === 'selection';
    if (!isSelectionVisible) return;

    const el = selectionAreaRef.current;
    if (!el) return;

    const width = el.getBoundingClientRect().width;
    const labelFontSizePx =
      selectionLabelFontSize ?? mobileSelectionLabelSize ?? defaultSelectionLabelSize;
    const minCardWidthPx = minSelectionCardWidthPx({
      labelFontSizePx,
      flagBoxFill: selectionFlagBoxFill,
      cardPy: selectionCompact ? 1 : 1.5,
      cardPx: selectionCompact ? 1.5 : 2,
      cardGap: selectionCompact ? 1.5 : 2,
      minCardHeightPx: selectionCompact ? 40 : 48,
    });

    setSelectionGridColumns({
      seeds: selectionColumnsForWidth(
        width,
        selectionGridGap,
        minCardWidthPx,
        selectionTeamCounts.seeds,
        selectionColumns,
      ),
      intermediates: selectionColumnsForWidth(
        width,
        selectionGridGap,
        minCardWidthPx,
        selectionTeamCounts.intermediates,
        selectionColumns,
      ),
      outsiders: selectionColumnsForWidth(
        width,
        selectionGridGap,
        minCardWidthPx,
        selectionTeamCounts.outsiders,
        selectionColumns,
      ),
    });
  };

  useLayoutEffect(() => {
    measureRankRows();
    measureSelectionColumns();
    const nodes = [
      rankColumnListRef.current,
      rankBodyRef.current,
      countryBodyRef.current,
      selectionAreaRef.current,
      rankSeedsSpacerRef.current,
    ].filter(Boolean) as HTMLElement[];
    if (nodes.length === 0) return;
    const ro = new ResizeObserver(() => {
      measureRankRows();
      measureSelectionColumns();
    });
    for (const n of nodes) ro.observe(n);
    return () => ro.disconnect();
  }, [
    props.mobileProps?.panel,
    isMobileEdit,
    isMobileRanking,
    listValid,
    countryList.length,
    props.compactRank,
    props.hideStepHeadings,
    alignRankToSelection,
    effectiveSelectionGridColumns.seeds,
    effectiveSelectionGridColumns.intermediates,
    effectiveSelectionGridColumns.outsiders,
    selectionGridGap,
    selectionLabelFontSize,
    selectionFlagBoxFill,
    selectionCompact,
    mobileSelectionLabelSize,
    defaultSelectionLabelSize,
    selectionColumns,
    selectionTeamCounts.seeds,
    selectionTeamCounts.intermediates,
    selectionTeamCounts.outsiders,
  ]);

  function toggleCountry(country: Country) {
    if (readOnly) return;
    if (countryList.includes(country)) {
      setCountryList(countryList.filter((x) => x !== country));
    } else {
      const newList = Array.from(countryList);
      newList.push(country);
      setCountryList(newList);
    }
  }

  const countryListFull = countryList.length >= 10;
  const canSelectFavourite =
    !countryListFull &&
    countryList.filter((x) => tournamentCountriesInfo[x].category === countryCategory.Favourite).length < 4;

  // --- Selection grids: Seeds / Intermediates / Outsiders ---

  const favouriteToggleCards = Object.values(tournamentCountriesInfo)
    .filter((x) => x.category === countryCategory.Favourite)
    .map((x) => (
      <ToggleCountryCard
        key={x.id}
        country={x.id}
        canBeSelected={canSelectFavourite}
        isSelected={countryList.includes(x.id)}
        onClick={() => toggleCountry(x.id)}
        readOnly={readOnly}
        flagBoxFill={selectionFlagBoxFill}
        labelFontSize={selectionLabelFontSize}
        minCardHeight={selectionCompact ? 10 : undefined}
        cardPy={selectionCompact ? 1 : undefined}
        nameSingleLine
        cardGap={isMobileEdit ? 1.5 : undefined}
        cardPx={isMobileEdit ? 1.5 : undefined}
      />
    ));
  const intermediateToggleCards = Object.values(tournamentCountriesInfo)
    .filter((x) => x.category === countryCategory.Intermediate)
    .map((x) => (
      <ToggleCountryCard
        key={x.id}
        country={x.id}
        canBeSelected={!countryListFull}
        isSelected={countryList.includes(x.id)}
        onClick={() => toggleCountry(x.id)}
        readOnly={readOnly}
        flagBoxFill={selectionFlagBoxFill}
        labelFontSize={selectionLabelFontSize}
        minCardHeight={selectionCompact ? 10 : undefined}
        cardPy={selectionCompact ? 1 : undefined}
        nameSingleLine
        cardGap={isMobileEdit ? 1.5 : undefined}
        cardPx={isMobileEdit ? 1.5 : undefined}
      />
    ));
  const outsiderToggleCards = Object.values(tournamentCountriesInfo)
    .filter((x) => x.category === countryCategory.Outsider)
    .map((x) => (
      <ToggleCountryCard
        key={x.id}
        country={x.id}
        canBeSelected={!countryListFull}
        isSelected={countryList.includes(x.id)}
        onClick={() => toggleCountry(x.id)}
        readOnly={readOnly}
        flagBoxFill={selectionFlagBoxFill}
        labelFontSize={selectionLabelFontSize}
        minCardHeight={selectionCompact ? 10 : undefined}
        cardPy={selectionCompact ? 1 : undefined}
        nameSingleLine
        cardGap={isMobileEdit ? 1.5 : undefined}
        cardPx={isMobileEdit ? 1.5 : undefined}
      />
    ));

  const mobileRankCardMinW = isMobileRanking ? '0' : rankLayout.cardMinW;

  const selectionArea = (
    <VStack ref={selectionAreaRef} spacing={selectionSectionSpacing} align='stretch'>
      {isMobileEdit && props.mobileProps?.panel === 'selection' ? (
        <Box flexShrink={0}>
          <Heading fontSize='md' mb={1}>
            Select your teams
          </Heading>
          <Text fontSize='sm' color={helperTextColor}>
            Pick 10 teams: max 4 seeds, at least 2 outsiders.
          </Text>
        </Box>
      ) : null}
      <SeedsSectionLabel />
      <SimpleGrid columns={effectiveSelectionGridColumns.seeds} gap={selectionGridGap} w='full' pb={2}>
        {favouriteToggleCards}
      </SimpleGrid>
      <Heading fontSize='sm' textAlign='start' width='full'>
        Intermediates
      </Heading>
      <SimpleGrid columns={effectiveSelectionGridColumns.intermediates} gap={selectionGridGap} w='full' pb={2}>
        {intermediateToggleCards}
      </SimpleGrid>
      <Heading fontSize='sm' textAlign='start' width='full'>
        Outsiders (min 2)
      </Heading>
      <SimpleGrid columns={effectiveSelectionGridColumns.outsiders} gap={selectionGridGap} w='full'>
        {outsiderToggleCards}
      </SimpleGrid>
    </VStack>
  );

  const rowPx = rankRowHeightPx;
  const slotsTotalPx = RANK_ROW_COUNT * rowPx + (RANK_ROW_COUNT - 1) * rankLayout.gapPx;

  const listCards = countryList.map((x, i) => (
    <EditListCountryCard
      key={x}
      index={i}
      country={x}
      isDisabled={readOnly || !listValid}
      rankList={isDesktopRank}
      rowHeightPx={rowPx}
      cardMinW={mobileRankCardMinW}
      flagBoxFill={rankFlagBoxFill}
      nameSingleLine={rankNameSingleLine}
      labelFontSize={rankLabelFontSize}
      touchDragHandle={isMobileRanking}
    />
  ));
  const listNumbers = Array.from(Array(RANK_ROW_COUNT).keys()).map((i) => (
    <Center
      key={i}
      flexShrink={0}
      h={`${rowPx}px`}
      minH={`${rowPx}px`}
      maxH={`${rowPx}px`}
      py={0}
    >
      <Heading fontSize={rankNumberSize}>{i + 1}</Heading>
    </Center>
  ));

  const rankListHeightPx = alignRankToSelection ? slotsTotalPx : undefined;

  const listArea = (
    <HStack
      flex={alignRankToSelection ? 'none' : 1}
      minH={alignRankToSelection ? undefined : 0}
      minW={0}
      h={alignRankToSelection ? 'auto' : 'full'}
      align='stretch'
      alignSelf={alignRankToSelection ? 'auto' : 'stretch'}
      spacing={4}
      width='full'
      pl={isMobileEdit ? 3 : 0}
      pr={isMobileEdit ? 3 : 0}
    >
      <Box
        ref={rankBodyRef}
        flexShrink={0}
        alignSelf='stretch'
        h={alignRankToSelection ? `${slotsTotalPx}px` : 'full'}
        minH={alignRankToSelection ? `${slotsTotalPx}px` : rankListCompactLayout ? 0 : `${slotsTotalPx}px`}
        maxH={alignRankToSelection ? `${slotsTotalPx}px` : undefined}
        minW={0}
        display='flex'
        flexDirection='column'
      >
        <VStack
          spacing={rankStackSpacing}
          align='stretch'
          h={alignRankToSelection || rankListCompactLayout ? 'full' : 'full'}
          minH={alignRankToSelection ? `${slotsTotalPx}px` : rankListCompactLayout ? undefined : `${slotsTotalPx}px`}
          flex={rankListCompactLayout ? 'none' : undefined}
        >
          {listNumbers}
        </VStack>
      </Box>
      {/* --- Ranking list (react-beautiful-dnd) --- */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='list' isDropDisabled={readOnly || !listValid}>
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              flex={1}
              minH={0}
              minW={0}
              h={alignRankToSelection ? `${slotsTotalPx}px` : 'full'}
              display='flex'
              flexDirection='column'
            >
              <Box
                ref={countryBodyRef}
                flex={1}
                minH={alignRankToSelection ? `${slotsTotalPx}px` : rankListCompactLayout ? 0 : `${slotsTotalPx}px`}
                maxH={alignRankToSelection ? `${slotsTotalPx}px` : undefined}
                minW={0}
                display='flex'
                flexDirection='column'
              >
                <VStack
                  spacing={rankStackSpacing}
                  align='stretch'
                  width='full'
                  minW={mobileRankCardMinW}
                  h={alignRankToSelection || rankListCompactLayout ? 'full' : 'full'}
                  minH={alignRankToSelection ? `${slotsTotalPx}px` : rankListCompactLayout ? undefined : `${slotsTotalPx}px`}
                >
                  {listCards}
                  {provided.placeholder}
                </VStack>
              </Box>
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </HStack>
  );

  function onDragEnd(result: DropResult, provided: ResponderProvided) {
    if (readOnly) return;
    if (result.source.droppableId === 'list' && result.destination?.droppableId === 'list') {
      const newList = Array.from(countryList);
      const country = newList.splice(result.source.index, 1)[0];
      newList.splice(result.destination.index, 0, country);
      setCountryList(newList);
    }
  }

  if (props.mobileProps) {
    if (props.mobileProps.panel === 'selection') {
      return selectionArea;
    }
    return (
      <Box flex={1} minH={0} h='full' display='flex' flexDirection='column'>
        <Box flexShrink={0} mb={3}>
          <Heading fontSize='md' mb={1}>
            Rank your teams
          </Heading>
          <Text fontSize='sm' color={helperTextColor}>
            Drag rows to set priority — #1 is highest.
          </Text>
        </Box>
        {listArea}
      </Box>
    );
  }

  return (
    <HStack
      spacing={4}
      flex={alignRankToSelection ? '0 1 auto' : 1}
      minH={alignRankToSelection ? undefined : 0}
      h={alignRankToSelection ? 'auto' : 'full'}
      align={alignRankToSelection ? 'flex-start' : 'stretch'}
      width='full'
      px={2}
    >
      <Box
        flex={alignRankToSelection ? '0 1 auto' : '1 1 0%'}
        minW={0}
        minH={0}
        display='flex'
        flexDirection='column'
      >
        {!props.hideStepHeadings ? (
          <Heading fontSize='md' mb={2} flexShrink={0}>
            1. Select Your Teams
          </Heading>
        ) : null}
        <Box flex={alignRankToSelection ? '0 0 auto' : 1} minH={0} overflowY={alignRankToSelection ? 'visible' : 'auto'}>
          {selectionArea}
        </Box>
      </Box>
      <Divider orientation='vertical' alignSelf='stretch' borderColor='chakra-border-color' flexShrink={0} />
      <Box
        flex={{ base: '1 1 0%', lg: `0 0 ${rankLayout.rankColumnWidth.lg}` }}
        w={{ base: 'full', lg: rankLayout.rankColumnWidth.lg }}
        maxW={{ lg: rankLayout.rankColumnWidth.lg }}
        minW={rankLayout.rankColumnMinW}
        minH={0}
        h={alignRankToSelection ? 'auto' : 'full'}
        alignSelf={alignRankToSelection ? 'flex-start' : 'stretch'}
        display='flex'
        flexDirection='column'
        opacity={readOnly || listValid ? 1 : 0.4}
      >
        {!props.hideStepHeadings ? (
          <Heading fontSize='md' mb={2} flexShrink={0}>
            2. Rank Teams
          </Heading>
        ) : null}
        <VStack
          spacing={showRankSeedsSpacer ? 3 : 0}
          align='stretch'
          flex={alignRankToSelection ? 'none' : 1}
          minH={alignRankToSelection ? undefined : 0}
          h={alignRankToSelection ? 'auto' : undefined}
          w='full'
          overflow={alignRankToSelection ? 'visible' : 'hidden'}
        >
          {showRankSeedsSpacer ? (
            <Box ref={rankSeedsSpacerRef} flexShrink={0} w='full'>
              <SeedsSectionLabel hidden />
            </Box>
          ) : null}
          <Box
            ref={rankColumnListRef}
            flex={alignRankToSelection ? 'none' : 1}
            flexShrink={0}
            h={alignRankToSelection && rankListHeightPx != null ? `${rankListHeightPx}px` : undefined}
            minH={alignRankToSelection && rankListHeightPx != null ? `${rankListHeightPx}px` : 0}
            maxH={alignRankToSelection && rankListHeightPx != null ? `${rankListHeightPx}px` : undefined}
            minW={0}
            w='full'
            display='flex'
            flexDirection='column'
            overflow={alignRankToSelection ? 'visible' : 'hidden'}
            alignItems='stretch'
            justifyContent='flex-start'
          >
            {listArea}
          </Box>
        </VStack>
      </Box>
    </HStack>
  );
}
